import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric6,
  type DashboardMetric6ProcessRiskBreakdown,
} from '@/services/dashboardService';
import { RiskRanking } from '@/services/types';

const { Text } = Typography;

// Fixed colors per requirement
const RISK_HIGH = '#EF4444'; // red
const RISK_MED = '#E6A700'; // yellow/gold
const RISK_LOW = '#22C55E'; // green
const RISK_UNRANKED = '#9CA3AF'; // grey

type Datum = {
  processId: string;
  processLabel: string; // "A01 Production Accounting"
  processCode: string;
  processName: string;
  high: number;
  medium: number;
  low: number;
  unranked: number;
  total: number;
};

export type EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChartProps = {
  height?: number;
  title?: string;
};

const Metric6Tooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const items = payload
    .filter((p: any) => typeof p?.value === 'number')
    .map((p: any) => ({ name: p.name, value: p.value, color: p.color }));

  const total = items.reduce((s: number, x: any) => s + (x.value ?? 0), 0);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

      {items.map((x: any) => (
        <div
          key={x.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: x.color,
              display: 'inline-block',
            }}
          />
          <span style={{ flex: 1 }}>{x.name}</span>
          <span style={{ fontWeight: 700 }}>{x.value}</span>
        </div>
      ))}

      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>Total</span>
        <span style={{ fontWeight: 800 }}>{total}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a bar to open Exhibit 5 filtered by Process + Risk Ranking
      </div>
    </div>
  );
};

export const EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChart: React.FC<
  EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChartProps
> = ({ height = 380, title = 'Risk Ranking of Controls by Process' }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric6ProcessRiskBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric6(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 6 (Control risk ranking breakdown by process).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Datum[] = useMemo(() => {
    return raw
      .map(p => {
        const high = p.highControlCount ?? 0;
        const medium = p.mediumControlCount ?? 0;
        const low = p.lowControlCount ?? 0;
        const unranked = p.unrankedControlCount ?? 0;

        return {
          processId: p.processId,
          processLabel: `${p.processCode} ${p.processName}`.trim(),
          processCode: p.processCode,
          processName: p.processName,
          high,
          medium,
          low,
          unranked,
          total: high + medium + low + unranked,
        };
      })
      //.sort((a, b) => b.total - a.total);
  }, [raw]);

  const maxTotal = useMemo(() => {
    return Math.max(0, ...data.map(d => d.high ?? 0),  ...data.map(d => d.medium ?? 0),  ...data.map(d => d.low ?? 0),  ...data.map(d => d.unranked ?? 0));
  }, [data]);

  const xTicks = useMemo(() => {
    // same pattern: 1..maxTotal (forces odd + even tick labels)
    return Array.from({ length: maxTotal }, (_, i) => i + 1);
  }, [maxTotal]);

  const riskTotals = useMemo(() => {
    return data.reduce(
      (acc, d) => {
        acc.high += d.high ?? 0;
        acc.medium += d.medium ?? 0;
        acc.low += d.low ?? 0;
        acc.unranked += d.unranked ?? 0;
        acc.total += (d.high ?? 0) + (d.medium ?? 0) + (d.low ?? 0) + (d.unranked ?? 0);
        return acc;
      },
      { high: 0, medium: 0, low: 0, unranked: 0, total: 0 },
    );
  }, [data]);

  const { totalControls, totalProcesses } = useMemo(() => {
        const totalProcesses = data.length;
    
        // safest: sum processCount (already provided by API)
        const totalControls = data.reduce(
          (sum, c) => sum + (c.total ?? 0),
          0,
        );
    
        return { totalControls, totalProcesses };
      }, [data]);
    
      const computedTitle =
        !loading && !errorMessage && data.length > 0
          ? `Risk Ranking of Controls (Total Count: ${totalControls}) by Process (Total Count: ${totalProcesses})`
          : title;

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Adjust query keys to match Exhibit 5 filtering
  const goToExhibit5 = (
    processId: string,
    processCode: string,
    processName: string,
    risk: RiskRanking,
  ) => {
    navigate('/control-activities', {
      state: {
        processId: processId,
        processCode: processCode,
        processName: processName,
        riskRanking: risk,
      },
    });
  };

  const computedHeight = useMemo(() => {
    const rowHeight = 55; // tweak 24–36 based on your font/spacing
    const min = height;
    const needed = data.length * rowHeight + 80; // padding for axes/legend
    return Math.max(min, needed);
  }, [data.length, height]);

  return (
    <Card title={computedTitle} extra={<Text type='secondary'></Text>}>
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 6 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No processes were returned.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              margin={{ top: 8, right: 140, bottom: 24, left: 32 }}
              barCategoryGap={14}
              barGap={4}
            >
              <XAxis
                type='number'
                domain={[0, maxTotal]}
                ticks={xTicks}
                allowDecimals={false}
                label={{
                  value: 'Count of Controls',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />
              <YAxis
                type='category'
                dataKey='processLabel'
                width={240}
                interval={0}
                tickMargin={8}
                label={{
                  value: 'Process Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -25,
                }}
              />

              <Tooltip content={<Metric6Tooltip />} />
              <Legend
                layout='vertical'
                align='right'
                verticalAlign='middle'
                wrapperStyle={{ paddingLeft: 16 }}
                formatter={(value: string) => {
                  switch (value) {
                    case 'High':
                      return `High (${riskTotals.high} - ${(riskTotals.high / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Medium':
                      return `Medium (${riskTotals.medium} - ${(riskTotals.medium / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Low':
                      return `Low (${riskTotals.low} - ${(riskTotals.low / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Unranked':
                      return `Unranked (${riskTotals.unranked} - ${(riskTotals.unranked / riskTotals.total * 100).toFixed(0)}%)`;
                    default:
                      return value;
                  }
                }}
              />

              <Bar
                dataKey='high'
                name='High'
                fill={RISK_HIGH}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'High');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='medium'
                name='Medium'
                fill={RISK_MED}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'Med');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='low'
                name='Low'
                fill={RISK_LOW}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'Low');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='unranked'
                name='Unranked'
                fill={RISK_UNRANKED}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'Unranked');
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChart;
